import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft, AlertTriangle, Search, RefreshCw, Loader2,
  Unlink, Link2, ChevronRight, PoundSterling,
} from "lucide-react";
import { toast } from "sonner";
import { getUserRole, ROLES } from "../../config/roleConfig";

// ─── shared helpers (mirrors InvoiceTracker.jsx) ─────────────────────────────

function authHeaders() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const email =
    user.user_email || user.email || user.ms_email ||
    sessionStorage.getItem("bonsai_user_email") ||
    localStorage.getItem("bonsai_user_email") || "";
  return {
    "X-Bonsai-User-Id":
      sessionStorage.getItem("bonsai_user_id") ||
      localStorage.getItem("bonsai_user_id") || "",
    "X-User-Email": email,
  };
}

function fmtGbp(raw) {
  if (raw === null || raw === undefined || raw === "") return "";
  const n = Number(typeof raw === "string" ? raw.replace(/[,\s]/g, "") : raw);
  if (!Number.isFinite(n)) return String(raw);
  return new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP",
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

// ─── Unlink Modal ──────────────────────────────────────────────────────────

function UnlinkModal({ row, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = reason.trim().length >= 3 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/hub/finance/invoice-requests/${row.invoice_request_id}/unlink`,
        { reason: reason.trim() },
        { headers: authHeaders() },
      );
      toast.success("Invoice request unlinked");
      onDone();
    } catch (err) {
      toast.error(errorMessage(err, "Unlink failed"));
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="unlink-modal"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-md w-full p-6">
        <h3 className="text-lg text-slate-100 mb-2">Unlink invoice request</h3>
        <p className="text-sm text-slate-400 mb-4">
          Clears the credit-control link on{" "}
          <span className="text-slate-200">
            {row.invoice_request_number || row.invoice_request_id}
          </span>
          . This will not affect the credit-control record itself.
        </p>
        <label className="flex flex-col gap-1 text-sm text-slate-300 mb-4">
          <span>Reason (min 3 chars, required)</span>
          <textarea
            data-testid="unlink-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-slate-100"
          />
        </label>
        <div className="flex justify-end gap-2">
          <Button
            data-testid="unlink-modal-cancel"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            data-testid="unlink-modal-confirm"
            onClick={submit}
            disabled={!canSubmit}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Unlink"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Relink Modal (search + pick + confirm) ────────────────────────────────

function RelinkModal({ row, onClose, onDone }) {
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [mismatchOverride, setMismatchOverride] = useState(false);

  const doSearch = async () => {
    if (query.trim().length < 2) return;
    setSearching(true);
    try {
      const res = await axios.get(
        `${API}/hub/finance/credit-control/search`,
        { headers: authHeaders(), params: { q: query.trim() } },
      );
      setCandidates(res.data?.results || []);
    } catch (err) {
      toast.error(errorMessage(err, "Search failed"));
    } finally {
      setSearching(false);
    }
  };

  const submit = async (force = false) => {
    if (!picked || reason.trim().length < 3 || busy) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/hub/finance/invoice-requests/${row.invoice_request_id}/relink`,
        {
          new_credit_control_id: picked.id,
          reason: reason.trim(),
          force,
        },
        { headers: authHeaders() },
      );
      toast.success("Invoice request relinked");
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error === "matter_ref_mismatch") {
        setMismatchOverride(true);
        toast.warning(
          "Matter reference mismatch — use 'Relink anyway' if you're sure.",
        );
      } else {
        toast.error(errorMessage(err, "Relink failed"));
      }
      setBusy(false);
    }
  };

  const canPrimary = picked && reason.trim().length >= 3 && !busy;

  return (
    <div
      data-testid="relink-modal"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full p-6">
        <h3 className="text-lg text-slate-100 mb-1">Relink invoice request</h3>
        <p className="text-sm text-slate-400 mb-4">
          Currently linked to CC{" "}
          <span className="text-slate-300">
            {row.linked_cc_matter_reference || `id ${row.linked_cc_id}`}
          </span>
          . Search below for the correct credit-control record.
        </p>

        <div className="flex gap-2 mb-3">
          <input
            data-testid="relink-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") doSearch(); }}
            placeholder="Matter ref, client name, or invoice number"
            className="flex-1 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-slate-100"
          />
          <Button
            data-testid="relink-search-submit"
            onClick={doSearch}
            disabled={query.trim().length < 2 || searching}
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        <div
          data-testid="relink-candidates"
          className="border border-slate-700 rounded max-h-56 overflow-y-auto mb-3"
        >
          {candidates.length === 0 && (
            <div className="p-3 text-sm text-slate-500">
              {searching ? "Searching…" : "No results yet — search above."}
            </div>
          )}
          {candidates.map((c) => (
            <button
              key={c.id}
              type="button"
              data-testid={`relink-candidate-${c.id}`}
              onClick={() => setPicked(c)}
              className={`w-full text-left px-3 py-2 border-b border-slate-800 hover:bg-slate-800 ${
                picked?.id === c.id ? "bg-slate-800" : ""
              }`}
            >
              <div className="text-sm text-slate-200">
                {c.matter_reference} · {c.client_name}
              </div>
              <div className="text-xs text-slate-500">
                {c.invoice_count} invoice(s) · {fmtGbp(c.total_outstanding)} outstanding
              </div>
            </button>
          ))}
        </div>

        <label className="flex flex-col gap-1 text-sm text-slate-300 mb-4">
          <span>Reason (min 3 chars, required)</span>
          <textarea
            data-testid="relink-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="rounded border border-slate-600 bg-slate-800 px-2 py-1 text-slate-100"
          />
        </label>

        <div className="flex justify-between items-center gap-2">
          <Button
            data-testid="relink-modal-cancel"
            variant="ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {mismatchOverride && (
              <Button
                data-testid="relink-modal-force"
                variant="outline"
                onClick={() => submit(true)}
                disabled={!canPrimary}
              >
                Relink anyway
              </Button>
            )}
            <Button
              data-testid="relink-modal-confirm"
              onClick={() => submit(false)}
              disabled={!canPrimary}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Relink"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function RelinkInvoiceRequests() {
  const navigate = useNavigate();
  const role = getUserRole();
  const isDirectorOrMD = role === ROLES.DIRECTOR || role === ROLES.MD;

  const [tab, setTab] = useState("mislinked");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeUnlink, setActiveUnlink] = useState(null);
  const [activeRelink, setActiveRelink] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/hub/finance/invoice-requests/mislinked`,
        { headers: authHeaders() },
      );
      setRows(res.data?.rows || []);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load mislinked list"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isDirectorOrMD) refresh();
  }, [isDirectorOrMD, refresh]);

  if (!isDirectorOrMD) {
    return (
      <div
        data-testid="relink-forbidden"
        className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6"
      >
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-status-amber mb-3" />
          <h2 className="text-lg mb-2">MD / Director only</h2>
          <p className="text-sm text-slate-400 mb-4">
            The invoice-request relink admin surface is restricted to MD and
            director accounts.
          </p>
          <Button
            data-testid="relink-forbidden-back"
            onClick={() => navigate("/")}
          >
            Back to home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            data-testid="relink-back"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl">Relink invoice requests</h1>
          <div className="flex-1" />
          <Button
            data-testid="relink-refresh"
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={loading}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>

        <div className="flex gap-2 mb-4 border-b border-slate-800">
          <button
            data-testid="relink-tab-mislinked"
            onClick={() => setTab("mislinked")}
            className={`px-4 py-2 text-sm ${
              tab === "mislinked"
                ? "border-b-2 border-teal text-teal"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Mislinked ({rows.length})
          </button>
          <button
            data-testid="relink-tab-search"
            onClick={() => setTab("search")}
            className={`px-4 py-2 text-sm ${
              tab === "search"
                ? "border-b-2 border-teal text-teal"
                : "text-slate-400 hover:text-slate-200"
            }`}
          >
            Search all
          </button>
        </div>

        {tab === "mislinked" && (
          <div data-testid="relink-mislinked-panel">
            {loading && (
              <div className="text-center py-8 text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading mislinked invoice requests…
              </div>
            )}
            {!loading && rows.length === 0 && (
              <div
                data-testid="relink-empty"
                className="text-center py-12 text-slate-500"
              >
                No mislinked invoice requests. Everything is in order.
              </div>
            )}
            {!loading && rows.length > 0 && (
              <table
                data-testid="relink-mislinked-table"
                className="w-full text-sm"
              >
                <thead className="text-left text-slate-400 border-b border-slate-800">
                  <tr>
                    <th className="py-2 px-3">Invoice #</th>
                    <th className="py-2 px-3">IR matter ref</th>
                    <th className="py-2 px-3">Linked CC matter ref</th>
                    <th className="py-2 px-3">Reason</th>
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.invoice_request_id}
                      data-testid={`relink-row-${r.invoice_request_id}`}
                      className="border-b border-slate-800/50 hover:bg-slate-900/50"
                    >
                      <td className="py-2 px-3 text-slate-200">
                        {r.invoice_request_number || r.invoice_request_id.slice(0, 8)}
                      </td>
                      <td className="py-2 px-3">{r.matter_reference}</td>
                      <td className="py-2 px-3 text-status-amber">
                        {r.reason === "linked_cc_missing"
                          ? "(record missing)"
                          : r.linked_cc_matter_reference}
                      </td>
                      <td className="py-2 px-3">
                        <span className="text-xs px-2 py-0.5 rounded bg-status-red/15 text-status-red">
                          {r.reason.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right space-x-2">
                        <Button
                          data-testid={`relink-row-unlink-${r.invoice_request_id}`}
                          size="sm"
                          variant="ghost"
                          onClick={() => setActiveUnlink(r)}
                        >
                          <Unlink className="w-3.5 h-3.5 mr-1" />
                          Unlink
                        </Button>
                        <Button
                          data-testid={`relink-row-relink-${r.invoice_request_id}`}
                          size="sm"
                          onClick={() => setActiveRelink(r)}
                        >
                          <Link2 className="w-3.5 h-3.5 mr-1" />
                          Relink
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "search" && (
          <SearchAllTab
            onUnlink={setActiveUnlink}
            onRelink={setActiveRelink}
          />
        )}
      </div>

      {activeUnlink && (
        <UnlinkModal
          row={activeUnlink}
          onClose={() => setActiveUnlink(null)}
          onDone={() => { setActiveUnlink(null); refresh(); }}
        />
      )}
      {activeRelink && (
        <RelinkModal
          row={activeRelink}
          onClose={() => setActiveRelink(null)}
          onDone={() => { setActiveRelink(null); refresh(); }}
        />
      )}
    </div>
  );
}

// ─── Search All Tab (looks up any invoice request by matter/client) ──────

function SearchAllTab({ onUnlink, onRelink }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (q.trim().length < 2) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/submissions`,
        {
          headers: authHeaders(),
          params: { form_type: "invoice_request", search: q.trim(), limit: 50 },
        },
      );
      setResults(res.data?.submissions || res.data?.items || []);
    } catch (err) {
      toast.error(errorMessage(err, "Search failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="relink-search-panel">
      <div className="flex gap-2 mb-4">
        <input
          data-testid="relink-search-all-input"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          placeholder="Search all invoice requests by matter ref, client, or number"
          className="flex-1 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-slate-100"
        />
        <Button
          data-testid="relink-search-all-submit"
          onClick={search}
          disabled={q.trim().length < 2 || loading}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        </Button>
      </div>

      {results.length === 0 && !loading && (
        <div className="text-center py-8 text-slate-500 text-sm">
          Search above to view existing invoice-request links.
        </div>
      )}

      {results.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left text-slate-400 border-b border-slate-800">
            <tr>
              <th className="py-2 px-3">Invoice #</th>
              <th className="py-2 px-3">IR matter ref</th>
              <th className="py-2 px-3">Linked CC id</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {results.map((s) => {
              const row = {
                invoice_request_id: s.id,
                invoice_request_number: (s.data || {}).invoice_request_number || "",
                matter_reference: (s.data || {}).Q4 || s.matter_reference || "",
                linked_cc_id: (s.data || {}).credit_control_id || "",
                linked_cc_matter_reference: "",
              };
              return (
                <tr
                  key={s.id}
                  data-testid={`relink-search-row-${s.id}`}
                  className="border-b border-slate-800/50 hover:bg-slate-900/50"
                >
                  <td className="py-2 px-3 text-slate-200">
                    {row.invoice_request_number || s.id.slice(0, 8)}
                  </td>
                  <td className="py-2 px-3">{row.matter_reference}</td>
                  <td className="py-2 px-3 text-slate-400">
                    {row.linked_cc_id || <span className="text-slate-600">(unlinked)</span>}
                  </td>
                  <td className="py-2 px-3 text-right space-x-2">
                    <Button
                      data-testid={`relink-search-unlink-${s.id}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => onUnlink(row)}
                      disabled={!row.linked_cc_id}
                    >
                      Unlink
                    </Button>
                    <Button
                      data-testid={`relink-search-relink-${s.id}`}
                      size="sm"
                      onClick={() => onRelink(row)}
                    >
                      Relink
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
